import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import {
	AutomationStepAction,
	AutomationEditorStep,
	EditAutomationTemplateContext,
	EditAutomationTemplateStepsContext,
	IEditAutomationTemplateStepsContext,
} from '../../../../models/Automations';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useLambda } from '../../../../models/hooks/useLambda';
import { useEventLogging } from '../../../../models/Logging';
import { useAutomationTemplateStepSwitchRemoveCaseMutation } from '../../../../queries/Automation/mutations/switchCases/useAutomationTemplateStepSwitchRemoveCaseMutation';
import { useAutomationTemplateStepSwitchUpdateCaseMutation } from '../../../../queries/Automation/mutations/switchCases/useAutomationTemplateStepSwitchUpdateCaseMutation';
import { useAutomationTemplateQuery } from '../../../../queries/Automation/queries/useAutomationTemplateQuery';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { useAutomationEditorSteps } from '../../../containers/EditAutomationTemplate/hooks';
import { navigation } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { LoadingSpinner } from '../../LoadingSpinner';
import { AutomationTemplateStepIndicatorIcon } from '../../svgs/icons/AutomationTemplateStepIndicator';
import { EditItemPenIcon } from '../../svgs/icons/EditItemPenIcon';
import { RoundAddIcon } from '../../svgs/icons/RoundAddIcon';
import { TextInputModal } from '../../TextInputModal';
import { EditAutomationTemplateStepCardHeader } from '../AutomationStepCard/presentation';
import { EditAutomationTemplateItemBumpers } from '../EditAutomationTemplateItemBumpers';
import { EditAutomationTemplateSegmentHeader } from '../EditAutomationTemplateSegmentHeader';
import { EditAutomationTemplateSteps } from '../EditAutomationTemplateSteps';
import { styleSheet } from './styles';

interface IEditAutomationTemplateSegmentProps {
	caseStatement: Api.IAutomationStepCaseStatement;
	switchStep: AutomationEditorStep;
	onStepSaved: () => void;
}

export const EditAutomationTemplateSegment: React.FC<IEditAutomationTemplateSegmentProps> = props => {
	const { switchStep, caseStatement, onStepSaved } = props;
	const parentImpersonationContext = switchStep?.impersonationContext;
	const parentTemplateId = switchStep?.templateId || switchStep?.automationStep?.automationTemplate?.id;

	// Load the child template using React Query
	const childTemplateQuery = useAutomationTemplateQuery({
		id: caseStatement?.automationTemplateId || '',
		enabled: !!caseStatement?.automationTemplateId,
		impersonationContext: parentImpersonationContext,
	});

	// Use the queried template data, falling back to legacy sources
	const automationTemplate =
		childTemplateQuery.data ||
		(caseStatement as any)?.automationTemplate ||
		(switchStep?.automationStep as any)?.automationTemplate ||
		switchStep?.automationStep?.getTemplateForCase?.(caseStatement);

	const { onDraftUpdated, onChildTemplatesLoaded } = React.useContext(EditAutomationTemplateContext);
	const stepsContext = React.useContext(EditAutomationTemplateStepsContext);
	const { logApiError, logEvent } = useEventLogging('EditAutomationSegment');
	const errorMessages = useErrorMessages();
	const index = caseStatement
		? (switchStep?.automationStep?.cases?.findIndex(
				(x: Api.IAutomationStepCaseStatement) => x.id === caseStatement.id
			) ?? -1)
		: -1;

	// React Query mutations for case operations
	const removeCaseMutation = useAutomationTemplateStepSwitchRemoveCaseMutation({
		onSuccess: () => {
			onDraftUpdated?.();
			onStepSaved?.();
		},
		onError: error => {
			logApiError('RemoveSwitchStepCase-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	// Use ref to avoid infinite loop - mutation objects are unstable references
	const removeCaseMutationRef = React.useRef(removeCaseMutation);
	removeCaseMutationRef.current = removeCaseMutation;

	const updateCaseMutation = useAutomationTemplateStepSwitchUpdateCaseMutation({
		onSuccess: () => {
			onStepSaved?.();
		},
		onError: error => {
			logApiError('UpdateSwitchStepCase-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	// Use the custom hook for managing steps
	const { steps, isLoading, addStep, loadSteps, setStepsState } = useAutomationEditorSteps({
		templateId: automationTemplate?.id,
		impersonationContext: parentImpersonationContext,
	});

	const isLoaded = !isLoading && !childTemplateQuery.isLoading && steps.length > 0;

	React.useEffect(() => onChildTemplatesLoaded?.(), [onChildTemplatesLoaded]);

	// Load steps when template changes
	React.useEffect(() => {
		if (automationTemplate) {
			logEvent('EditAutomationTemplateStepsLoad');
			loadSteps(automationTemplate, true).catch(error => {
				logApiError('EditAutomationTemplateStepsLoad-Error', error);
				errorMessages.pushApiError(error);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [automationTemplate?.id]);

	// Create a shim object that provides ViewModel-like interface for context compatibility
	const editorStepsShim = React.useMemo(() => {
		if (!automationTemplate) {
			return null;
		}
		return {
			steps,
			isLoaded,
			automationTemplate: {
				...automationTemplate,
				canEdit: automationTemplate.canEdit ?? true, // Default to true for child templates
			},
			children: new Api.ObservableCollection(null, 'uuid'),
			canReorderSteps: steps.every(x => !!x.automationStep?.id),
			canReoderSteps: steps.every(x => !!x.automationStep?.id),
			setStepsState,
		};
	}, [steps, isLoaded, automationTemplate, setStepsState]);

	// Create context value with the shim
	const editorStepsContext = React.useMemo<IEditAutomationTemplateStepsContext>(
		() => ({
			editorStepElementRefs: {},
			editorSteps: editorStepsShim as any,
		}),
		[editorStepsShim]
	);

	// Register this editor as a child of the parent editor
	// Only register once on mount to avoid infinite re-render loops
	React.useEffect(() => {
		if (editorStepsShim && stepsContext?.editorSteps?.children) {
			const collection = stepsContext.editorSteps.children;
			if (!collection.has(editorStepsShim as any)) {
				collection.add(editorStepsShim as any);
			}
			return () => {
				collection.removeItems([editorStepsShim as any]);
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Empty deps - register once only

	const onAddStepClicked = React.useCallback(async () => {
		await addStep();
		onStepSaved?.();
	}, [addStep, onStepSaved]);

	const onMenuItemClicked = React.useCallback(
		async (action: AutomationStepAction) => {
			switch (action) {
				case AutomationStepAction.Delete: {
					const nonDefaultSteps =
						switchStep.automationStep?.cases?.filter((x: Api.IAutomationStepCaseStatement) => !x.isDefault) || [];
					if (nonDefaultSteps.length === 1) {
						// bubble up and remove the switch step
						try {
							logEvent('RemoveSwitchStep');
							await stepsContext?.editorSteps?.executeActionForStep(switchStep, action);
							onDraftUpdated?.();
							onStepSaved?.();
						} catch (error) {
							logApiError('RemoveSwitchStep-Error', error);
							errorMessages.pushApiError(error);
						}
					} else {
						// remove just the case/segment
						logEvent('RemoveSwitchStepCase', {
							caseStatementId: caseStatement.id,
						});

						if (parentTemplateId && switchStep.automationStep?.id && caseStatement.id) {
							await removeCaseMutationRef.current.mutateAsync({
								templateId: parentTemplateId,
								stepId: switchStep.automationStep.id,
								caseId: caseStatement.id,
								impersonationContext: parentImpersonationContext,
							});
						}
					}
					break;
				}
				default: {
					break;
				}
			}
		},
		[
			stepsContext?.editorSteps,
			switchStep,
			caseStatement,
			parentTemplateId,
			parentImpersonationContext,
			onDraftUpdated,
			onStepSaved,
			logEvent,
			logApiError,
			errorMessages,
		]
	);
	// #endregion

	// #region rename modal
	const [renameModalProps, setRenameModalProps, setRenamModalLambda] = useLambda(
		{
			isOpen: false,
			onRequestClose: null,
		},
		[]
	);
	const renameModalPropsRef = React.useRef(renameModalProps);
	renameModalPropsRef.current = renameModalProps;

	// Use ref to avoid infinite loop - mutation objects are unstable references
	const updateCaseMutationRef = React.useRef(updateCaseMutation);
	updateCaseMutationRef.current = updateCaseMutation;

	const onRenameModalRequestClose = React.useCallback(
		async (nextName?: string, canceled?: boolean) => {
			const name = nextName?.trim();
			setRenameModalProps({
				...renameModalPropsRef.current,
				isOpen: false,
			});
			if (!canceled && !!name) {
				logEvent('RenameSegment', {
					caseStatementId: caseStatement?.id,
					nameLength: name.length,
				});

				if (parentTemplateId && switchStep.automationStep?.id && caseStatement?.id) {
					await updateCaseMutationRef.current.mutateAsync({
						templateId: parentTemplateId,
						stepId: switchStep.automationStep.id,
						caseId: caseStatement.id,
						caseData: {
							...(caseStatement || {}),
							name,
						},
						impersonationContext: parentImpersonationContext,
					});
				}
			}
		},
		[caseStatement, switchStep, parentTemplateId, parentImpersonationContext, logEvent, setRenameModalProps]
	);
	React.useEffect(() => {
		setRenameModalProps({
			...renameModalPropsRef.current,

			onRequestClose: onRenameModalRequestClose,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onRenameModalRequestClose]);
	// #endregion

	const onRenderTitle = React.useCallback(() => {
		return (
			<div className={css(baseStyleSheet.horizontalStack)}>
				<div className={css(baseStyleSheet.truncateText, styleSheet.cardHeaderTitleName)}>
					{`Segment ${index + 1}: ${caseStatement?.name || 'Add name'}`}
				</div>
				<button
					className={css(styleSheet.segmentEditNameButton)}
					onClick={setRenamModalLambda({ ...renameModalProps, isOpen: true })}
				>
					<EditItemPenIcon />
				</button>
			</div>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [renameModalProps, caseStatement?.name, index]);

	const styles = React.useRef([styleSheet.segment]).current;

	// Segments always use the switch step icon since they're children of a switch step
	const segmentIconStep = React.useMemo(() => {
		return { _type: Api.AutomationStepType.Switch } as any;
	}, []);

	const onRenderLeftBumper = React.useCallback(() => {
		return (
			<div className={css(styleSheet.indicator)}>
				<AutomationTemplateStepIndicatorIcon
					step={segmentIconStep}
					type='default'
					styles={[styleSheet.indicatorIcon]}
				/>
				<div className={css(styleSheet.indicatorHorizontalLine)} />
				<div className={css(styleSheet.indicatorVerticalLine, styleSheet.indicatorVerticalLineBottom)} />
				<div className={css(styleSheet.indicatorVerticalLine, styleSheet.indicatorVerticalLineTop)} />
			</div>
		);
	}, [segmentIconStep]);

	const cardHeaderStyles = React.useRef<StyleDeclarationValue[]>([styleSheet.segmentCardHeader]).current;
	return (
		<EditAutomationTemplateItemBumpers
			onRenderLeftBumperContent={onRenderLeftBumper}
			scrollAnchorId={caseStatement?.id}
			styles={styles}
		>
			<EditAutomationTemplateStepCardHeader
				canEdit={automationTemplate?.canEdit}
				ActionMenu={
					caseStatement?.isDefault ? null : (
						<Dropdown>
							<DropdownEllipsisTrigger />
							<DropdownContent>
								<DropdownItem onClick={() => onMenuItemClicked(AutomationStepAction.Delete)}>
									Delete Segment
								</DropdownItem>
							</DropdownContent>
						</Dropdown>
					)
				}
				onRenderTitle={onRenderTitle}
				styles={cardHeaderStyles}
			/>
			{isLoaded ? (
				<EditAutomationTemplateStepsContext.Provider value={editorStepsContext}>
					<EditAutomationTemplateSegmentHeader
						templateId={parentTemplateId}
						stepId={switchStep?.automationStep?.id}
						impersonationContext={parentImpersonationContext}
						caseStatement={caseStatement}
						switchStep={switchStep?.automationStep?.model}
						onStepSaved={onStepSaved}
					/>
					<div className={css(styleSheet.segmentSteps)}>
						<EditAutomationTemplateSteps onStepSaved={onStepSaved} />
					</div>
					<div className={css(styleSheet.segmentStepsFooter)}>
						<button
							className={css(baseStyleSheet.horizontalStack, styleSheet.segmentAddStepButton)}
							onClick={onAddStepClicked}
						>
							<RoundAddIcon fillColor={navigation} />
							<span>Add another step for this segment</span>
						</button>
					</div>
				</EditAutomationTemplateStepsContext.Provider>
			) : (
				<LoadingSpinner type='small' />
			)}

			{index >= 0 && (
				<TextInputModal
					cta='Rename'
					initialValue={caseStatement?.name}
					modalProps={renameModalProps}
					placeholderText='Please enter a name...'
					title={`Rename Segment ${index + 1}`}
					type='text'
				/>
			)}
		</EditAutomationTemplateItemBumpers>
	);
};
